import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { MakeOptInOptOutDecisionRequest, makeOptInOptOutDecision } from 'core'

import { Button, Col, DatePicker, Modal, Notification, Row } from 'common/components'
import { now } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    effectiveDate: Yup.date()
      .nullable()
      .typeError(translate('general.required'))
      .required(translate('general.required')),
  })

interface OptInOptOutModalProps {
  visible: boolean
  onCancel: () => void
  title: string
  description: ReactNode
  action: string
  danger?: boolean
  isLoading?: boolean
  userToOptIn: MakeOptInOptOutDecisionRequest
  afterSuccessTerminate: () => void
  benefitEligibilityDate: Date
}

const OptInOptOutModal: React.FC<OptInOptOutModalProps> = (props) => {
  const { t: translate } = useTranslation('translation')
  const [loading, setLoading] = useState<boolean>(false)
  let submitFormHandler: () => Promise<any> = () => new Promise(() => {})
  const [isFormValid, setIsFormValid] = useState<boolean>(props.action === 'Opt-out')

  const { sendCommand: sendMakeDecision, isLoading: isMakeDecisionLoading } = useCommandRequestV2(
    (request: MakeOptInOptOutDecisionRequest) => makeOptInOptOutDecision(request),
  )

  const onSubmit = async (values: MakeOptInOptOutDecisionRequest) => {
    setLoading(true)
    const result = await sendMakeDecision(values)
    setLoading(false)

    if (result.completed) {
      props.afterSuccessTerminate()
    } else {
      Notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
  }

  const modalFooterActions = [
    <Button key="back" onClick={props.onCancel}>
      Cancel
    </Button>,
    <Button
      key="submit"
      type="primary"
      danger={props.danger}
      onClick={() => submitFormHandler()}
      loading={props.isLoading}
      disabled={!isFormValid}
    >
      {props.action}
    </Button>,
  ]

  return (
    <Modal
      title={props.title}
      visible={props.visible}
      destroyOnClose
      closable
      onCancel={props.onCancel}
      footer={modalFooterActions}
    >
      {props.description}
      <div className="mt-16">
        <Formik
          enableReinitialize={true}
          validationSchema={props.action === 'Opt-out' ? null : formValidator(translate)}
          initialValues={props.userToOptIn}
          onSubmit={onSubmit}
          validate={(values) => {
            setIsFormValid(props.action === 'Opt-out' || formValidator(translate).isValidSync(values))
          }}
        >
          {(formik) => {
            const { handleSubmit, submitForm } = formik
            submitFormHandler = submitForm
            return (
              <form onSubmit={handleSubmit} autoComplete="off">
                <Row className={props.action === 'Opt-out' ? 'd-none' : ''}>
                  <Col span={24}>
                    <DatePicker
                      label={
                        props.action === 'Opt-in'
                          ? translate('admin-dashboard.manage-people.opt-in-date')
                          : translate('admin-dashboard.manage-people.opt-out-date')
                      }
                      name="effectiveDate"
                      id="effectiveDate"
                      disabledDate={(d) => !d || d.isAfter(now()) || d.isBefore(props.benefitEligibilityDate)}
                      noStyles
                    />
                  </Col>
                </Row>
              </form>
            )
          }}
        </Formik>
      </div>
    </Modal>
  )
}

export default OptInOptOutModal
